if (!customElements.get('product-form-component')) {
  customElements.define(
    'product-form-component',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.init();
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.querySelector('form');
        if (!this.form) {
          this.initialized = false;
          return;
        }
        // Re-enable the variant id input only when NOT in bulk mode.
        // Bulk mode disables it intentionally via Liquid; standard mode needs it enabled.
        if (!this.isBulkMode()) {
          this.variantIdInput.disabled = false;
        }
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer');
        this.submitButton = this.form?.querySelector('.add-to-cart-button');
        this.submitButtonText = this.submitButton ? this.submitButton.querySelector('span') : null;
        this.checkbox = this.form?.querySelector('[id^="agree_condition-"]');
        this.buyItNowButton = this.form?.querySelector('.shopify-payment-button');

        if (document.querySelector('cart-drawer') && this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.initAgreeCondition();

        // Sync error messages to sticky add-to-cart error area (same section)
        this.setupErrorSync();
      }

      setupErrorSync() {
        try {
          const productInfo = this.closest('product-info');
          const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
          if (!sectionId) return;

          const stickyATC = document.querySelector(`sticky-atc[data-sticky-section-id="${sectionId}"]`);
          if (!stickyATC) return;

          const mainErrorWrapper = this.querySelector('.product-form__error-message-wrapper');
          const stickyErrorWrapper = stickyATC.querySelector('.product-form__error-message-wrapper');
          if (!mainErrorWrapper || !stickyErrorWrapper) return;

          const syncFromMain = () => {
            const isVisible = !mainErrorWrapper.hasAttribute('hidden');
            const errorMessage = mainErrorWrapper.querySelector('.product-form__error-message')?.textContent;
            stickyErrorWrapper.toggleAttribute('hidden', !isVisible || !errorMessage);
            if (isVisible && errorMessage) {
              const stickyErrorMessage = stickyErrorWrapper.querySelector('.product-form__error-message');
              if (stickyErrorMessage) stickyErrorMessage.textContent = errorMessage;
            }
          };

          // Initial sync
          syncFromMain();

          // Observe changes on main error wrapper
          this._errorObserver = new MutationObserver(syncFromMain);
          this._errorObserver.observe(mainErrorWrapper, {
            attributes: true,
            attributeFilter: ['hidden'],
            childList: true,
            subtree: true,
          });

          // Also listen to cartError pub/sub to copy message
          this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, () => {
            syncFromMain();
          });
        } catch (e) {
          // silent
        }
      }

      initAgreeCondition() {
        if (!this.checkbox || !this.buyItNowButton) return;

        this.buyItNowButton.classList.add('disabled');

        this.checkbox.addEventListener('change', () => {
          this.buyItNowButton.classList.toggle('disabled', !this.checkbox.checked);
        });

        this.form.addEventListener('submit', (e) => {
          if (!this.checkbox.checked) {
            e.preventDefault();
          }
        });
      }

      // ─── Mode detection ──────────────────────────────────────────────────────
      // Returns true when the form contains bulk-list quantity inputs
      // (items[N][quantity]). When false, the standard Shopify path is used.
      getFormAssociatedInputs(selector) {
        console.log('[ProductForm] getFormAssociatedInputs called with selector:', selector);
        if (!this.form) {
          console.log('[ProductForm] getFormAssociatedInputs: this.form is null/undefined');
          return [];
        }
        console.log('[ProductForm] getFormAssociatedInputs: form ID is:', this.form.id);

        try {
          if (this.form.elements) {
            const elements = Array.from(this.form.elements);
            console.log('[ProductForm] getFormAssociatedInputs: form.elements count:', elements.length);
            const associated = elements.filter(el => el.matches && el.matches(selector));
            console.log('[ProductForm] getFormAssociatedInputs: matches found via form.elements:', associated.length);
            if (associated.length > 0) return associated;
          }
        } catch (e) {
          console.error('[ProductForm] Error matching form elements:', e);
        }

        try {
          const id = this.form.id;
          if (id) {
            const docQuery = Array.from(document.querySelectorAll(`${selector}[form="${id}"], [id="${id}"] ${selector}`));
            console.log('[ProductForm] getFormAssociatedInputs: matches found via document query:', docQuery.length);
            return docQuery;
          }
        } catch (e) {
          console.error('[ProductForm] Error querying associated inputs:', e);
        }

        try {
          const formQuery = Array.from(this.form.querySelectorAll(selector));
          console.log('[ProductForm] getFormAssociatedInputs: matches found inside form:', formQuery.length);
          return formQuery;
        } catch (e) {
          console.error('[ProductForm] Error querying form inputs:', e);
          return [];
        }
      }

      isBulkMode() {
        const isBulk = this.getFormAssociatedInputs('input[name^="items["][name*="[quantity]"]').length > 0;
        console.log('[ProductForm] isBulkMode returned:', isBulk);
        return isBulk;
      }

      // ─── Entry point ─────────────────────────────────────────────────────────
      onSubmitHandler(evt) {
        evt.preventDefault();
        console.log('[ProductForm] onSubmitHandler triggered');
        if (this.submitButton.getAttribute('aria-disabled') === 'true') {
          console.log('[ProductForm] submitButton is disabled, aborting');
          return;
        }

        // Reset error state from any previous submission so it never bleeds through.
        this.error = false;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');

        // Reflect loading state on sticky add-to-cart button (if present)
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        if (sectionId) {
          const stickyBtn = document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`);
          stickyBtn?.querySelector('.loading__spinner')?.classList.remove('hidden');
          stickyBtn?.classList.add('loading');
        }

        if (this.isBulkMode()) {
          this.submitBulk();
        } else {
          this.submitStandard();
        }
      }

      // ─── Standard path (original Shopify Dawn implementation) ────────────────
      // Do NOT add bulk-specific logic here.
      // Exact copy of the original Shopify Dawn implementation — do NOT modify.
      submitStandard() {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Collect custom product properties (file inputs and text inputs)
        const properties = document.querySelectorAll('.product-form__input [name^="properties"]');
        properties.forEach((property) => {
          if (property.type === 'file') {
            if (!property.files || property.files.length === 0 || !property.files[0]) return;
            formData.append(property.name, property.files[0]);
            return;
          }
          if (property.value == null || property.value === '') return;
          formData.append(property.name, property.value);
        });

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            this.handleCartResponse(response, formData.get('id'));
          })
          .catch((e) => console.error(e))
          .finally(() => this.finalizeSubmit(false));
      }

      // ─── Bulk path (custom multi-variant table) ──────────────────────────────
      // Do NOT add standard-variant logic here.
      submitBulk() {
        const formData = new FormData(this.form);
        const tempItems = {};

        formData.forEach((value, key) => {
          const match = key.match(/^items\[(\d+)\]\[(\w+)\]$/);
          if (match) {
            const index = match[1];
            const prop  = match[2];
            if (!tempItems[index]) tempItems[index] = {};
            tempItems[index][prop] = value;
          }
        });

        const items = Object.values(tempItems).reduce((acc, item) => {
          if (item && item.id && item.quantity) {
            const qty = parseInt(item.quantity);
            const variantId = parseInt(item.id);
            if (!isNaN(qty) && qty > 0 && !isNaN(variantId)) {
              acc.push({ id: variantId, quantity: qty });
            }
          }
          return acc;
        }, []);

        if (items.length === 0) {
          this.handleErrorMessage('Please select at least one item.');
          this.finalizeSubmit(false);
          return;
        }

        const payload = { items };
        if (this.cart) {
          payload.sections      = this.cart.getSectionsToRender().map((s) => s.id);
          payload.sections_url  = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.headers['Content-Type']     = 'application/json';
        config.body = JSON.stringify(payload);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            this.handleCartResponse(response, 'bulk');
          })
          .catch((e) => console.error(e))
          .finally(() => {
            this.finalizeSubmit(true); // true = reset bulk quantities on success
          });
      }

      // ─── Shared: handle Shopify /cart/add response ───────────────────────────
      handleCartResponse(response, variantId) {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form-component',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.handleErrorMessage(response.description);

          const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
          if (!soldOutMessage) return;
          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButtonText.classList.add('hidden');
          soldOutMessage.classList.remove('hidden');
          this.error = true;
          return;
        }

        if (!this.cart) {
          window.location = `${window.routes?.cart_url}`;
          return;
        }

        if (!this.error) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form-component',
            productVariantId: variantId,
            cartData: response,
          });
        }
        this.error = false;

        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                if (this.cart && typeof this.cart.renderContents === 'function') {
                  this.cart.renderContents(response);
                }
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          if (this.cart && typeof this.cart.renderContents === 'function') {
            this.cart.renderContents(response);
          }
        }
      }

      // ─── Shared: clean up loading states after fetch completes ───────────────
      // resetBulk: when true, zero out bulk quantities (only for the bulk path on success)
      finalizeSubmit(resetBulk = false) {
        this.submitButton.classList.remove('loading');
        if (this.cart && this.cart.classList.contains('is-empty')) {
          this.cart.classList.remove('is-empty');
        }
        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
          if (resetBulk) this.resetBulkSelections();
        }
        this.submitButton.querySelector('.loading__spinner').classList.add('hidden');

        // Clear loading state on the sticky add-to-cart button (if present)
        const productInfo = this.closest('product-info');
        const sectionId = productInfo?.dataset.section || productInfo?.dataset.originalSection;
        if (sectionId) {
          const stickyBtn = document.getElementById(`StickyCart-ProductSubmitButton-${sectionId}`);
          stickyBtn?.classList.remove('loading');
          stickyBtn?.querySelector('.loading__spinner')?.classList.add('hidden');
        }
      }

      // ─── Bulk-specific: reset all quantity inputs to 0 after add ─────────────
      resetBulkSelections() {
        if (!this.form) return;

        // Reset bulk-list quantity inputs (items[N][quantity])
        this.getFormAssociatedInputs('input[name^="items["]').forEach((input) => {
          if (input.name.includes('[quantity]')) {
            input.value = 0;
            // Update the visible number displayed by the quantity stepper (if present)
            const wrapper = input.closest('.quantity-selector, .quantity__wrapper, [class*="quantity"]');
            if (wrapper) {
              const display = wrapper.querySelector('.quantity__input, input[type="number"]');
              if (display && display !== input) display.value = 0;
            }
            // Notify any listeners (e.g. price summary, button state)
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }

      disconnectedCallback() {
        try {
          this.cartErrorUnsubscriber?.();
          this._errorObserver?.disconnect();
        } catch (e) {
          // silent
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
